import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TrackerLayout, { refreshAdminNotifs } from '../../components/TrackerLayout';
import { useAdminSession } from '../../context/TrackerContext';
import { getClassOverReports, markClassOverSeen, undoClassOver } from '../../lib/api';
import type { ClassOverReport, ClassOverStatus } from '../../lib/api';

const rupees  = (n: number) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDay  = (d: string) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtWhen = (iso: string | null) => iso
  ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
  : '';
const roleLabel = (role: string) => role === 'Student Coordinator' ? 'Coordinator' : role === 'Senior' ? 'Senior Mentor' : 'Mentor';

const STATUS_STYLE: Record<ClassOverStatus, { bg: string; color: string }> = {
  New:    { bg: '#fef3c7', color: '#92400e' },
  Seen:   { bg: '#e0f2fe', color: '#0369a1' },
  Undone: { bg: '#f3f4f6', color: '#6b7280' },
};

// ── Main Page ─────────────────────────────────────────────────────────────────
// Class Over raised by mentors (for their own students) and Student
// Coordinators. It has already taken effect — the student is inactive, their
// payment is Class Over and their timetable slots are free — and admin was
// emailed the class report. Mark it seen to clear the notification, or undo it.

const ClassOverReports: React.FC = () => {
  const { adminPassword } = useAdminSession();
  const [filter, setFilter]     = useState<ClassOverStatus | 'All'>('New');
  const [reports, setReports]   = useState<ClassOverReport[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading]   = useState(true);
  const [busy, setBusy]         = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClassOverReports(adminPassword, filter);
      setReports(res.reports ?? []);
      setNewCount(res.newCount ?? 0);
    } catch (err: any) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  }, [adminPassword, filter]);

  useEffect(() => { load(); }, [load]);

  const seen = async (r?: ClassOverReport) => {
    setBusy(r?._id ?? 'all');
    try {
      const res = await markClassOverSeen(adminPassword, r?._id);
      toast.success(r ? `Marked ${r.studentName} as seen` : `Marked ${res.updated} as seen`);
      load();
      refreshAdminNotifs();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  const undo = async (r: ClassOverReport) => {
    if (!window.confirm(`Undo Class Over for ${r.studentName}?\n\nThey become Active again with ${r.mentorName}, their payment status goes back, and their timetable slots are restored.`)) return;
    setBusy(r._id);
    try {
      const res = await undoClassOver(adminPassword, r._id);
      toast.success(res.studentReactivated
        ? `${r.studentName} is active again${res.timetableSlots ? ` — ${res.timetableSlots} timetable slot${res.timetableSlots === 1 ? '' : 's'} restored` : ''}`
        : `Undone — ${r.studentName}'s status had already been changed, so it was left as it is`);
      load();
      refreshAdminNotifs();
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(null); }
  };

  return (
    <TrackerLayout>
      <div className="space-y-5">
        <div className="rounded-2xl overflow-hidden shadow-lg"
          style={{ background: 'linear-gradient(135deg,#2b2240 0%,#3d2f5f 50%,#2b2240 100%)' }}>
          <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-2xl">🎓</span>
                <h1 className="text-xl font-bold text-white">Class Over</h1>
              </div>
              <p className="text-white/50 text-xs max-w-2xl">
                Raised by mentors and Student Coordinators when a student finishes the course. The student is already
                deactivated and their payment marked Class Over — you were emailed the full class report. Undo it if it was a mistake.
              </p>
            </div>
            <div className="flex gap-2 self-start sm:self-auto">
              {newCount > 0 && (
                <button onClick={() => seen()} disabled={busy === 'all'}
                  className="px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-50"
                  style={{ background: '#F0B429', color: '#2b2240' }}>
                  ✓ Mark all seen
                </button>
              )}
              <button onClick={load}
                className="px-3 py-2 rounded-xl text-xs font-medium border border-white/20 text-white/70 hover:bg-white/10 transition-colors">
                ↻ Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          {(['New', 'Seen', 'Undone', 'All'] as const).map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className="text-xs font-semibold px-4 py-2 rounded-xl border-2 transition-colors"
              style={filter === s
                ? { background: '#2b2240', color: '#fff', borderColor: '#2b2240' }
                : { background: '#fff', color: '#6b7280', borderColor: '#e5e7eb' }}>
              {s}{s === 'New' && newCount > 0 ? ` (${newCount})` : ''}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-8 h-8 rounded-full border-purple-200 border-t-purple-600 animate-spin" style={{ borderWidth: 3 }} />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-14 text-center">
            <div className="text-5xl mb-3">✅</div>
            <p className="text-gray-700 font-bold text-base">{filter === 'New' ? 'No new Class Over' : 'Nothing here yet'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map(r => {
              const st = STATUS_STYLE[r.status];
              const attPct = r.totalClasses ? Math.round((r.present / r.totalClasses) * 100) : 0;
              const isBusy = busy === r._id;
              return (
                <div key={r._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
                  style={r.status === 'New' ? { borderLeftWidth: 4, borderLeftColor: '#F0B429' } : {}}>
                  <div className="p-5 space-y-3">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-gray-800">{r.studentName}</p>
                          <span className="text-xs text-gray-400">({r.studentId})</span>
                          <span className="text-[11px] px-2 py-0.5 rounded-md font-semibold" style={{ background: '#ede9fe', color: '#5b21b6' }}>{r.course}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Raised by <b>{r.raisedByName}</b> ({roleLabel(r.raisedByRole)}) · {fmtWhen(r.createdAt)}
                          {r.raisedBy !== r.mentorId && <> · Mentor: {r.mentorName}</>}
                        </p>
                      </div>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg" style={{ background: st.bg, color: st.color }}>{r.status}</span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { label: 'Classes', value: `${r.totalClasses} (${r.present} present)`, color: '#2b2240' },
                        { label: 'Attendance', value: `${attPct}%`, color: attPct >= 80 ? '#15803d' : attPct >= 60 ? '#d97706' : '#dc2626' },
                        { label: 'Course dates', value: r.firstClass ? `${fmtDay(r.firstClass)} – ${fmtDay(r.lastClass)}` : 'No classes logged', color: '#2b2240' },
                        r.courseFee > 0
                          ? { label: 'Fee', value: r.balance > 0 ? `${rupees(r.balance)} still due` : 'Fully paid', color: r.balance > 0 ? '#dc2626' : '#15803d' }
                          : { label: 'Fee', value: 'No fee on record', color: '#6b7280' },
                      ].map(c => (
                        <div key={c.label} className="rounded-xl px-3 py-2" style={{ background: '#f8f6ff' }}>
                          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{c.label}</p>
                          <p className="text-sm font-bold" style={{ color: c.color }}>{c.value}</p>
                        </div>
                      ))}
                    </div>

                    {r.note && (
                      <div className="rounded-xl px-3 py-2 text-sm bg-gray-50 text-gray-700">
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wide mr-1.5">Note:</span>{r.note}
                      </div>
                    )}

                    {r.status === 'Undone' ? (
                      <p className="text-xs text-gray-400">Undone {fmtWhen(r.undoneAt)}</p>
                    ) : (
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => undo(r)} disabled={isBusy}
                          className="px-4 py-2 rounded-xl text-xs font-bold border-2 disabled:opacity-50"
                          style={{ borderColor: '#e5e7eb', color: '#6b7280' }}>
                          ↩ Undo
                        </button>
                        {r.status === 'New' && (
                          <button onClick={() => seen(r)} disabled={isBusy}
                            className="px-4 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50"
                            style={{ background: '#2b2240' }}>
                            {isBusy ? '…' : '✓ Mark seen'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </TrackerLayout>
  );
};

export default ClassOverReports;
